import React, { useState, useEffect } from "react";
import Sidebar from "../components/Sidebar";
import Header from "../components/Header";
import { Search, Plus, Edit2, Trash2, Layers, X, Briefcase, Video, Image as ImageIcon, Globe, LayoutList, LayoutGrid, Eye, UploadCloud, Copy, CheckCircle2, FolderOpen } from "lucide-react";
import Swal from 'sweetalert2';
import { API_BASE_URL } from "../config";

export default function BusinessManagement() {
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [editingBusinessId, setEditingBusinessId] = useState(null);
    const [editingBusinessData, setEditingBusinessData] = useState(null);
    const [newBusinessName, setNewBusinessName] = useState("");

    // Multi-Language File Selection States
    const [selectedHindiFiles, setSelectedHindiFiles] = useState([]);
    const [selectedEnglishFiles, setSelectedEnglishFiles] = useState([]);

    // Media preview modal (for image or video popup)
    const [previewMedia, setPreviewMedia] = useState(null);

    // Gallery Modal state
    const [galleryBusiness, setGalleryBusiness] = useState(null);
    const [galleryTab, setGalleryTab] = useState('all'); // 'all' | 'hindi' | 'english'

    const [businesses, setBusinesses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [businessNamesList, setBusinessNamesList] = useState([]);
    const [politicsNamesList, setPoliticsNamesList] = useState([]);
    const [createMode, setCreateMode] = useState('business');
    const [viewMode, setViewMode] = useState('table'); // 'table' or 'grid'

    const fetchBusinessNames = () => {
        fetch(`${API_BASE_URL}/api/business-names/all`)
            .then(res => res.json())
            .then(resJson => {
                if (resJson && resJson.status && resJson.data) {
                    setBusinessNamesList(resJson.data);
                }
            })
            .catch(err => console.error(err));
    };

    const fetchPoliticsNames = () => {
        fetch(`${API_BASE_URL}/api/politics-names/all`)
            .then(res => res.json())
            .then(resJson => {
                if (resJson && resJson.status && resJson.data) {
                    setPoliticsNamesList(resJson.data);
                }
            })
            .catch(err => console.error(err));
    };

    const fetchBusinesses = () => {
        setLoading(true);
        fetch(`${API_BASE_URL}/api/business/all-businesses`)
            .then((res) => {
                if (!res.ok) throw new Error("Failed to fetch businesses");
                return res.json();
            })
            .then((resJson) => {
                if (resJson && resJson.status && resJson.data) {
                    setBusinesses(resJson.data);
                } else {
                    throw new Error(resJson.message || "Invalid response format");
                }
                setLoading(false);
            })
            .catch((err) => {
                console.error("Businesses fetch error:", err);
                setError(err.message);
                setLoading(false);
            });
    };

    useEffect(() => {
        fetchBusinesses();
        fetchBusinessNames();
        fetchPoliticsNames();
    }, []);

    const resetForm = () => {
        setNewBusinessName("");
        setSelectedHindiFiles([]);
        setSelectedEnglishFiles([]);
        setEditingBusinessId(null);
        setEditingBusinessData(null);
        setIsCreateModalOpen(false);
    };

    const handleEditBusiness = (business) => {
        setNewBusinessName(business.business_name || "");
        setEditingBusinessId(business.business_id_PK);
        setEditingBusinessData(business);
        setSelectedHindiFiles([]);
        setSelectedEnglishFiles([]);

        if (politicsNamesList.some(p => p.name === business.business_name)) {
            setCreateMode('politics');
        } else {
            setCreateMode('business');
        }
        setIsCreateModalOpen(true);
    };

    const handleOpenAddForBusiness = (business) => {
        handleEditBusiness(business);
    };

    const handleOpenGallery = (business, tab = 'all') => {
        setGalleryBusiness(business);
        setGalleryTab(tab);
    };

    const handleUploadBothLanguages = (files) => {
        setSelectedHindiFiles(prev => [...prev, ...files]);
        setSelectedEnglishFiles(prev => [...prev, ...files]);
    };

    const handleCopyHindiToEnglish = () => {
        if (selectedHindiFiles.length === 0) {
            Swal.fire("Info", "No Hindi files selected to copy!", "info");
            return;
        }
        setSelectedEnglishFiles(prev => [...prev, ...selectedHindiFiles]);
        Swal.fire({
            icon: 'success',
            title: 'Copied!',
            text: `${selectedHindiFiles.length} file(s) copied to English.`,
            timer: 1500,
            showConfirmButton: false
        });
    };

    const handleCopyEnglishToHindi = () => {
        if (selectedEnglishFiles.length === 0) {
            Swal.fire("Info", "No English files selected to copy!", "info");
            return;
        }
        setSelectedHindiFiles(prev => [...prev, ...selectedEnglishFiles]);
        Swal.fire({
            icon: 'success',
            title: 'Copied!',
            text: `${selectedEnglishFiles.length} file(s) copied to Hindi.`,
            timer: 1500,
            showConfirmButton: false
        });
    };

    const handleSaveBusiness = () => {
        if (!newBusinessName.trim()) {
            Swal.fire("Error", "Please select or enter a business/politics name!", "error");
            return;
        }

        const hasNewMedia = selectedHindiFiles.length > 0 || selectedEnglishFiles.length > 0;
        if (!editingBusinessId && !hasNewMedia) {
            Swal.fire("Error", "Please upload at least one image or video for Hindi or English!", "error");
            return;
        }

        const formData = new FormData();
        formData.append("business_name", newBusinessName);

        // Append Hindi files
        selectedHindiFiles.forEach(file => {
            if (file.type && file.type.startsWith('video/')) {
                formData.append("business_video_hindi", file);
            } else {
                formData.append("business_image_hindi", file);
            }
        });

        // Append English files
        selectedEnglishFiles.forEach(file => {
            if (file.type && file.type.startsWith('video/')) {
                formData.append("business_video_english", file);
            } else {
                formData.append("business_image_english", file);
            }
        });

        Swal.fire({
            title: editingBusinessId ? "Updating..." : "Creating...",
            text: "Uploading files to cloud, please wait.",
            allowOutsideClick: false,
            didOpen: () => Swal.showLoading()
        });

        const url = editingBusinessId
            ? `${API_BASE_URL}/api/business/update-business/${editingBusinessId}`
            : `${API_BASE_URL}/api/business/create-business`;
        const method = editingBusinessId ? "PUT" : "POST";

        fetch(url, { method, body: formData })
            .then(res => {
                if (!res.ok) throw new Error("Failed to save business");
                return res.json();
            })
            .then(resJson => {
                Swal.close();
                if (resJson && resJson.status) {
                    Swal.fire("Success", `Business entry ${editingBusinessId ? 'updated' : 'created'} successfully!`, "success");
                    fetchBusinesses();
                    resetForm();
                } else {
                    throw new Error(resJson.message || "Failed to save business");
                }
            })
            .catch(err => {
                Swal.close();
                console.error("Save business error:", err);
                Swal.fire("Error", err.message, "error");
            });
    };

    const handleRemoveMedia = (business_id_PK, targetUrl, type = "media") => {
        Swal.fire({
            title: 'Remove Media?',
            text: `Are you sure you want to remove this ${type}?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, remove it!'
        }).then((result) => {
            if (result.isConfirmed) {
                Swal.fire({ title: "Removing...", allowOutsideClick: false, didOpen: () => Swal.showLoading() });
                fetch(`${API_BASE_URL}/api/business/remove-media`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ business_id_PK, targetUrl })
                })
                    .then(res => res.json())
                    .then(resJson => {
                        Swal.close();
                        if (resJson && resJson.status) {
                            Swal.fire('Removed!', 'Media has been removed successfully.', 'success');

                            // If modal is open, also remove from editingBusinessData
                            if (editingBusinessData && editingBusinessData.business_id_PK === business_id_PK) {
                                const updateObj = { ...editingBusinessData };
                                if (updateObj.Hindi) {
                                    updateObj.Hindi.images = updateObj.Hindi.images.filter(u => u !== targetUrl);
                                    updateObj.Hindi.videos = updateObj.Hindi.videos.filter(u => u !== targetUrl);
                                }
                                if (updateObj.English) {
                                    updateObj.English.images = updateObj.English.images.filter(u => u !== targetUrl);
                                    updateObj.English.videos = updateObj.English.videos.filter(u => u !== targetUrl);
                                }
                                if (updateObj.Default) {
                                    updateObj.Default.images = updateObj.Default.images.filter(u => u !== targetUrl);
                                    updateObj.Default.videos = updateObj.Default.videos.filter(u => u !== targetUrl);
                                }
                                setEditingBusinessData(updateObj);
                            }
                            fetchBusinesses();
                        } else {
                            throw new Error(resJson.message || "Failed to remove media");
                        }
                    })
                    .catch(err => {
                        Swal.close();
                        Swal.fire('Error', err.message, 'error');
                    });
            }
        });
    };

    const handleDeleteBusiness = (id) => {
        Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this! All media for this business will be deleted.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!'
        }).then((result) => {
            if (result.isConfirmed) {
                Swal.fire({ title: "Deleting...", allowOutsideClick: false, didOpen: () => Swal.showLoading() });
                fetch(`${API_BASE_URL}/api/business/delete-business/${id}`, { method: "DELETE" })
                    .then(res => {
                        if (!res.ok) throw new Error("Failed to delete business");
                        return res.json();
                    })
                    .then(resJson => {
                        Swal.close();
                        if (resJson && resJson.status) {
                            Swal.fire('Deleted!', 'Business entry has been deleted.', 'success');
                            fetchBusinesses();
                        } else {
                            throw new Error(resJson.message || "Failed to delete business");
                        }
                    })
                    .catch(err => {
                        Swal.close();
                        console.error("Delete error:", err);
                        Swal.fire("Error", err.message, "error");
                    });
            }
        });
    };

    const renderMediaThumbnails = (mediaList = [], isVideo = false, badgeText = "H", badgeColor = "bg-blue-600", businessId = null, canDelete = false) => {
        if (!mediaList || mediaList.length === 0) return null;
        return (
            <div className="flex items-center gap-1.5 flex-wrap">
                {mediaList.map((url, idx) => (
                    <div key={`${badgeText}-${idx}`} className="relative group/thumb flex-shrink-0">
                        {isVideo ? (
                            <div
                                onClick={() => setPreviewMedia({ url, type: 'video' })}
                                className="w-12 h-12 rounded-lg bg-black border border-slate-200 dark:border-slate-700 cursor-pointer overflow-hidden flex items-center justify-center relative hover:opacity-90 transition-opacity"
                            >
                                <video src={url} className="w-full h-full object-cover" />
                                <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                                    <Video size={16} className="text-white" />
                                </div>
                            </div>
                        ) : (
                            <img
                                src={url}
                                alt="thumb"
                                onClick={() => setPreviewMedia({ url, type: 'image' })}
                                className="w-12 h-12 rounded-lg object-cover border border-slate-200 dark:border-slate-700 cursor-pointer hover:opacity-80 transition-opacity"
                            />
                        )}
                        <span className={`absolute -bottom-1.5 -right-1 text-[9px] font-bold text-white px-1 py-0.2 rounded shadow ${badgeColor}`}>
                            {badgeText}
                        </span>
                        {canDelete && businessId && (
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleRemoveMedia(businessId, url, isVideo ? 'video' : 'image');
                                }}
                                className="absolute -top-1.5 -left-1.5 bg-rose-600 text-white p-0.5 rounded-full opacity-0 group-hover/thumb:opacity-100 transition-opacity shadow-sm hover:bg-rose-700 z-10"
                                title="Remove file"
                            >
                                <X size={12} strokeWidth={3} />
                            </button>
                        )}
                    </div>
                ))}
            </div>
        );
    };

    const renderCompactMediaCell = (images = [], videos = [], lang = 'Hindi', badgeColor = 'bg-blue-600', borderClass = 'border-blue-500', business) => {
        const totalCount = images.length + videos.length;
        if (totalCount === 0) {
            return (
                <div className="flex items-center gap-2 text-slate-400 text-xs py-1">
                    <span className="w-2 h-2 rounded-full bg-slate-300 dark:bg-slate-600"></span>
                    <span className="italic">No {lang} media</span>
                </div>
            );
        }

        const firstImage = images[0];
        const firstVideo = videos[0];
        const firstMedia = firstImage || firstVideo;
        const isVideo = !firstImage && Boolean(firstVideo);
        const remainingCount = totalCount - 1;

        return (
            <div className="flex items-center gap-3">
                {/* Main Clickable Thumbnail with Language Badge */}
                <div
                    className="relative group cursor-pointer flex-shrink-0"
                    onClick={() => handleOpenGallery(business, lang.toLowerCase())}
                    title={`Click to view all ${totalCount} ${lang} files`}
                >
                    <div className={`w-12 h-12 rounded-xl overflow-hidden border-2 ${borderClass} bg-slate-900 shadow-sm relative group-hover:scale-105 transition-transform`}>
                        {isVideo ? (
                            <video src={firstMedia} className="w-full h-full object-cover" />
                        ) : (
                            <img src={firstMedia} alt={lang} className="w-full h-full object-cover" />
                        )}
                        <div className="absolute inset-0 bg-black/10 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                            {isVideo && <Video size={14} className="text-white drop-shadow" />}
                        </div>
                    </div>
                    {/* Language Badge */}
                    <span className={`absolute -bottom-1 -right-1 text-[9px] font-bold text-white px-1.5 py-0.2 rounded shadow ${badgeColor}`}>
                        {lang.charAt(0)}
                    </span>
                </div>

                {/* Counts and View All link */}
                <div className="flex flex-col gap-0.5">
                    <div className="flex items-center gap-1.5">
                        <span className="text-xs font-bold text-slate-800 dark:text-slate-100">
                            {images.length} {images.length === 1 ? 'img' : 'imgs'}
                        </span>
                        {videos.length > 0 && (
                            <span className="text-xs font-medium text-slate-500 dark:text-slate-400">
                                • {videos.length} vid
                            </span>
                        )}
                    </div>
                    <button
                        type="button"
                        onClick={() => handleOpenGallery(business, lang.toLowerCase())}
                        className="text-[11px] font-semibold text-blue-600 dark:text-blue-400 hover:text-blue-700 flex items-center gap-1 text-left group/btn"
                    >
                        <Eye size={12} className="group-hover/btn:scale-110 transition-transform" />
                        <span>{remainingCount > 0 ? `+${remainingCount} more (View)` : 'View'}</span>
                    </button>
                </div>
            </div>
        );
    };

    return (
        <div className="bg-[#f8fafc] dark:bg-slate-900 min-h-screen text-slate-800 dark:text-slate-100 font-sans selection:bg-blue-100 dark:selection:bg-blue-900 relative overflow-hidden">
            <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

            <div className="flex-1 lg:ml-64 flex flex-col min-h-screen relative z-10 w-full lg:w-[calc(100%-16rem)]">
                <Header
                    onMenuClick={() => setIsSidebarOpen(true)}
                    title="Business Management"
                    subtitle="Manage multi-language images and videos for Business & Politics categories."
                />

                <div className="p-4 sm:p-8 flex-1 flex flex-col w-full overflow-hidden">
                    {/* Top Action Bar */}
                    <div className="mb-6 sm:mb-8 flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                        <div className="relative w-full sm:w-72">
                            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                            <input
                                type="text"
                                placeholder="Search by business name..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full bg-white dark:bg-slate-800/80 backdrop-blur-md border border-slate-200 dark:border-slate-700 shadow-sm rounded-xl pl-9 pr-4 py-2.5 text-sm outline-none focus:border-blue-500 transition-all dark:text-slate-100"
                            />
                        </div>

                        <div className="flex items-center gap-3 w-full lg:w-auto">
                            {/* View Mode Toggle */}
                            <div className="flex items-center bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-1 shadow-sm">
                                <button
                                    onClick={() => setViewMode('table')}
                                    className={`p-1.5 rounded-lg text-sm font-medium transition-all flex items-center gap-1.5 ${viewMode === 'table' ? 'bg-blue-50 text-blue-600 dark:bg-blue-900/40 dark:text-blue-400' : 'text-slate-500 hover:text-slate-800 dark:text-slate-400'}`}
                                    title="Single Row Table View"
                                >
                                    <LayoutList size={16} />
                                    <span className="hidden sm:inline text-xs">Table Row</span>
                                </button>
                                <button
                                    onClick={() => setViewMode('grid')}
                                    className={`p-1.5 rounded-lg text-sm font-medium transition-all flex items-center gap-1.5 ${viewMode === 'grid' ? 'bg-blue-50 text-blue-600 dark:bg-blue-900/40 dark:text-blue-400' : 'text-slate-500 hover:text-slate-800 dark:text-slate-400'}`}
                                    title="Grid View"
                                >
                                    <LayoutGrid size={16} />
                                    <span className="hidden sm:inline text-xs">Cards</span>
                                </button>
                            </div>

                            <button
                                onClick={() => { resetForm(); setCreateMode('business'); setIsCreateModalOpen(true); }}
                                className="flex-1 lg:flex-none justify-center bg-blue-600 hover:bg-blue-700 text-white shadow-sm hover:shadow-md px-4 py-2.5 rounded-xl text-sm font-medium transition-all flex items-center gap-2"
                            >
                                <Plus size={16} />
                                Add Business Media
                            </button>
                            <button
                                onClick={() => { resetForm(); setCreateMode('politics'); setIsCreateModalOpen(true); }}
                                className="flex-1 lg:flex-none justify-center bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm hover:shadow-md px-4 py-2.5 rounded-xl text-sm font-medium transition-all flex items-center gap-2"
                            >
                                <Plus size={16} />
                                Add Politics Media
                            </button>
                        </div>
                    </div>

                    {error && <div className="mb-6 p-4 bg-rose-50 border border-rose-100 text-rose-600 rounded-2xl">{error}</div>}

                    {loading ? (
                        <div className="flex items-center justify-center py-16 flex-1">
                            <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400 font-medium">
                                <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                                Loading business entries...
                            </div>
                        </div>
                    ) : businesses.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-16 flex-1 text-slate-500 dark:text-slate-400">
                            <Briefcase size={40} className="stroke-1 mb-2 text-slate-400" />
                            <p className="text-base font-semibold">No business entries found.</p>
                            <p className="text-xs text-slate-400 mt-1">Click "Add Business Media" to upload Hindi and English media.</p>
                        </div>
                    ) : viewMode === 'table' ? (
                        /* ================= SINGLE ROW TABLE VIEW ("ak hi row me add ho") ================= */
                        <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden flex-1 flex flex-col">
                            <div className="overflow-x-auto flex-1">
                                <table className="w-full text-left border-collapse whitespace-nowrap min-w-max">
                                    <thead className="bg-slate-50/70 dark:bg-slate-900/50 border-b border-slate-100 dark:border-slate-700/50">
                                        <tr>
                                            <th className="p-4 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider sticky left-0 bg-slate-50 dark:bg-slate-900/50 z-20 shadow-[1px_0_0_rgba(0,0,0,0.05)]">
                                                Business / Category Name
                                            </th>
                                            <th className="p-4 text-xs font-semibold text-blue-600 dark:text-blue-400 uppercase tracking-wider">
                                                <div className="flex items-center gap-1.5">
                                                    <span className="w-2.5 h-2.5 rounded-full bg-blue-500"></span>
                                                    Hindi Media (H)
                                                </div>
                                            </th>
                                            <th className="p-4 text-xs font-semibold text-emerald-600 dark:text-emerald-400 uppercase tracking-wider">
                                                <div className="flex items-center gap-1.5">
                                                    <span className="w-2.5 h-2.5 rounded-full bg-emerald-500"></span>
                                                    English Media (E)
                                                </div>
                                            </th>

                                            <th className="p-4 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider text-right sticky right-0 bg-slate-50 dark:bg-slate-900/50 z-10">
                                                Actions
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100 dark:divide-slate-700/50">
                                        {businesses
                                            .filter(b => (b.business_name || '').toLowerCase().includes(searchTerm.toLowerCase()))
                                            .map((business) => {
                                                const hindiImages = business.Hindi?.images || [];
                                                const hindiVideos = business.Hindi?.videos || [];
                                                const engImages = business.English?.images || [];
                                                const engVideos = business.English?.videos || [];
                                                const defaultImages = business.Default?.images || [];
                                                const defaultVideos = business.Default?.videos || [];

                                                const isPolitics = politicsNamesList.some(p => p.name === business.business_name);

                                                return (
                                                    <tr key={business.business_id_PK} className="hover:bg-slate-50/80 dark:hover:bg-slate-700/50 transition-colors group">
                                                        {/* Business Name Column */}
                                                        <td className="p-4 sticky left-0 bg-white dark:bg-slate-800 group-hover:bg-slate-50 dark:group-hover:bg-slate-700/50 z-10 shadow-[1px_0_0_rgba(0,0,0,0.05)] transition-colors">
                                                            <div className="flex items-center gap-3">
                                                                <div className={`w-9 h-9 rounded-xl flex items-center justify-center font-bold text-white shadow-sm ${isPolitics ? 'bg-emerald-600' : 'bg-blue-600'}`}>
                                                                    {business.business_name ? business.business_name.charAt(0).toUpperCase() : 'B'}
                                                                </div>
                                                                <div>
                                                                    <div className="font-bold text-slate-800 dark:text-slate-100 flex items-center gap-2">
                                                                        {business.business_name || "Untitled"}
                                                                        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${isPolitics ? 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800' : 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/40 dark:text-blue-300 dark:border-blue-800'}`}>
                                                                            {isPolitics ? 'Politics' : 'Business'}
                                                                        </span>
                                                                    </div>
                                                                    <span className="text-xs text-slate-400">
                                                                        Total Media: {(hindiImages.length + hindiVideos.length + engImages.length + engVideos.length + defaultImages.length + defaultVideos.length)} files
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        </td>

                                                        {/* Hindi Media Column */}
                                                        <td className="p-4">
                                                            {renderCompactMediaCell(hindiImages, hindiVideos, 'Hindi', 'bg-blue-600', 'border-blue-500', business)}
                                                        </td>

                                                        {/* English Media Column */}
                                                        <td className="p-4">
                                                            {renderCompactMediaCell(engImages, engVideos, 'English', 'bg-emerald-600', 'border-emerald-500', business)}
                                                        </td>

                                                        {/* Actions Column */}
                                                        <td className="p-4 text-right sticky right-0 bg-white dark:bg-slate-800 group-hover:bg-slate-50 dark:group-hover:bg-slate-700/50 z-10 shadow-[-1px_0_0_rgba(0,0,0,0.05)] transition-colors">
                                                            <div className="flex justify-end items-center gap-1.5">
                                                                <button
                                                                    onClick={() => handleOpenGallery(business, 'all')}
                                                                    title="View Full Media Gallery"
                                                                    className="p-2 text-slate-600 dark:text-slate-300 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 rounded-xl transition-colors flex items-center gap-1 text-xs font-semibold"
                                                                >
                                                                    <FolderOpen size={16} />
                                                                    <span className="hidden xl:inline">Gallery</span>
                                                                </button>
                                                                <button
                                                                    onClick={() => handleOpenAddForBusiness(business)}
                                                                    title="Add Media to this Business"
                                                                    className="p-2 text-slate-600 dark:text-slate-300 hover:text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/30 rounded-xl transition-colors flex items-center gap-1 text-xs font-semibold"
                                                                >
                                                                    <Plus size={16} />
                                                                    <span className="hidden xl:inline">Add Media</span>
                                                                </button>
                                                                <button
                                                                    onClick={() => handleEditBusiness(business)}
                                                                    title="Edit Business Entry"
                                                                    className="p-2 text-slate-600 dark:text-slate-300 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-xl transition-colors"
                                                                >
                                                                    <Edit2 size={16} />
                                                                </button>
                                                                <button
                                                                    onClick={() => handleDeleteBusiness(business.business_id_PK)}
                                                                    title="Delete Business Entry"
                                                                    className="p-2 text-rose-600 hover:text-rose-700 hover:bg-rose-50 dark:hover:bg-rose-900/30 rounded-lg transition-colors"
                                                                >
                                                                    <Trash2 size={16} />
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    ) : (
                        /* ================= CARD GRID VIEW ================= */
                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 overflow-y-auto pb-6">
                            {businesses
                                .filter(b => (b.business_name || '').toLowerCase().includes(searchTerm.toLowerCase()))
                                .map(business => {
                                    const hindiImages = business.Hindi?.images || [];
                                    const hindiVideos = business.Hindi?.videos || [];
                                    const engImages = business.English?.images || [];
                                    const engVideos = business.English?.videos || [];
                                    const defaultImages = business.Default?.images || [];
                                    const defaultVideos = business.Default?.videos || [];
                                    const isPolitics = politicsNamesList.some(p => p.name === business.business_name);

                                    return (
                                        <div key={business.business_id_PK} className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 p-6 flex flex-col hover:shadow-md transition-shadow">
                                            <div className="flex items-start justify-between mb-4">
                                                <div>
                                                    <h3 className="text-lg font-bold text-slate-800 dark:text-slate-100 flex items-center gap-2">
                                                        {business.business_name || "Untitled"}
                                                    </h3>
                                                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border inline-block mt-1 ${isPolitics ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-blue-50 text-blue-700 border-blue-200'}`}>
                                                        {isPolitics ? 'Politics' : 'Business'}
                                                    </span>
                                                </div>
                                                <div className="flex gap-1.5">
                                                    <button onClick={() => handleEditBusiness(business)} className="p-1.5 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                                                        <Edit2 size={16} />
                                                    </button>
                                                    <button onClick={() => handleDeleteBusiness(business.business_id_PK)} className="p-1.5 text-rose-500 hover:text-rose-700 hover:bg-rose-50 rounded-lg transition-colors">
                                                        <Trash2 size={16} />
                                                    </button>
                                                </div>
                                            </div>

                                            {/* Hindi Section */}
                                            <div className="mb-3 p-3 bg-blue-50/50 dark:bg-blue-950/20 rounded-xl border border-blue-100 dark:border-blue-900/30">
                                                <div className="text-xs font-bold text-blue-700 dark:text-blue-300 mb-2 flex items-center gap-1.5">
                                                    <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                                                    Hindi Media ({hindiImages.length + hindiVideos.length})
                                                </div>
                                                <div className="flex gap-2 overflow-x-auto pb-1 custom-scrollbar">
                                                    {hindiImages.length === 0 && hindiVideos.length === 0 ? (
                                                        <span className="text-xs text-slate-400 italic">No Hindi files</span>
                                                    ) : (
                                                        <>
                                                            {renderMediaThumbnails(hindiImages, false, 'H', 'bg-blue-600')}
                                                            {renderMediaThumbnails(hindiVideos, true, 'H', 'bg-blue-600')}
                                                        </>
                                                    )}
                                                </div>
                                            </div>

                                            {/* English Section */}
                                            <div className="mb-3 p-3 bg-emerald-50/50 dark:bg-emerald-950/20 rounded-xl border border-emerald-100 dark:border-emerald-900/30">
                                                <div className="text-xs font-bold text-emerald-700 dark:text-emerald-300 mb-2 flex items-center gap-1.5">
                                                    <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                                                    English Media ({engImages.length + engVideos.length})
                                                </div>
                                                <div className="flex gap-2 overflow-x-auto pb-1 custom-scrollbar">
                                                    {engImages.length === 0 && engVideos.length === 0 ? (
                                                        <span className="text-xs text-slate-400 italic">No English files</span>
                                                    ) : (
                                                        <>
                                                            {renderMediaThumbnails(engImages, false, 'E', 'bg-emerald-600')}
                                                            {renderMediaThumbnails(engVideos, true, 'E', 'bg-emerald-600')}
                                                        </>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Legacy / Default Section if exists */}
                                            {(defaultImages.length > 0 || defaultVideos.length > 0) && (
                                                <div className="p-3 bg-slate-50 dark:bg-slate-900/50 rounded-xl border border-slate-200 dark:border-slate-700">
                                                    <div className="text-xs font-bold text-slate-500 mb-2">Default Media ({defaultImages.length + defaultVideos.length})</div>
                                                    <div className="flex gap-2 overflow-x-auto pb-1 custom-scrollbar">
                                                        {renderMediaThumbnails(defaultImages, false, 'D', 'bg-slate-500')}
                                                        {renderMediaThumbnails(defaultVideos, true, 'D', 'bg-slate-500')}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                        </div>
                    )}
                </div>
            </div>

            {/* ================= CREATE / EDIT MODAL ================= */}
            {isCreateModalOpen && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={() => setIsCreateModalOpen(false)}></div>
                    <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-6 w-full max-w-3xl relative z-10 shadow-2xl overflow-y-auto max-h-[90vh] animate-in fade-in zoom-in duration-150">
                        <div className="flex justify-between items-center mb-6 pb-3 border-b border-slate-100 dark:border-slate-700">
                            <div>
                                <h3 className="text-lg font-bold text-slate-800 dark:text-slate-100">
                                    {editingBusinessId
                                        ? `Edit ${createMode === 'politics' ? 'Politics' : 'Business'} Media Entry`
                                        : `Add ${createMode === 'politics' ? 'Politics' : 'Business'} Media Entry`}
                                </h3>
                                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                                    Upload language-specific images and videos under a single entry.
                                </p>
                            </div>
                            <button onClick={() => setIsCreateModalOpen(false)} className="text-slate-400 hover:text-slate-600 p-1 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700">
                                <X size={20} />
                            </button>
                        </div>

                        <div className="space-y-6">
                            {/* Category Selector */}
                            <div>
                                <label className="block text-sm font-semibold text-slate-700 dark:text-slate-200 mb-1.5">
                                    {createMode === 'politics' ? 'Politics Category Name *' : 'Business Category Name *'}
                                </label>
                                <select
                                    value={newBusinessName}
                                    onChange={e => setNewBusinessName(e.target.value)}
                                    className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-blue-500 text-slate-800 dark:text-slate-100"
                                >
                                    <option value="">Select a {createMode === 'politics' ? 'politics' : 'business'} category</option>
                                    {(createMode === 'politics' ? politicsNamesList : businessNamesList).map((item) => (
                                        <option key={item.id} value={item.name}>
                                            {item.name}
                                        </option>
                                    ))}
                                    {/* Fallback option if editing an existing business whose name is custom */}
                                    {newBusinessName && !(createMode === 'politics' ? politicsNamesList : businessNamesList).find(b => b.name === newBusinessName) && (
                                        <option value={newBusinessName}>{newBusinessName}</option>
                                    )}
                                </select>
                            </div>

                            {/* SIMULTANEOUS UPLOAD TO BOTH LANGUAGES ("ak sath hindi english ke image add kro") */}
                            <div className="bg-gradient-to-r from-blue-50/80 via-indigo-50/60 to-emerald-50/80 dark:from-blue-950/40 dark:to-emerald-950/40 border border-blue-200/80 dark:border-blue-800/40 rounded-2xl p-4 shadow-sm">
                                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-2.5">
                                    <div>
                                        <h4 className="font-bold text-sm text-slate-800 dark:text-slate-100 flex items-center gap-2">
                                            <span className="flex h-2 w-2 relative">
                                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
                                                <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-600"></span>
                                            </span>
                                            Add to Both Languages Simultaneously (एक साथ दोनों में जोड़ें)
                                        </h4>
                                        <p className="text-xs text-slate-500 dark:text-slate-400">
                                            Select images/videos here to add them to both Hindi & English at the same time.
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <button
                                            type="button"
                                            onClick={handleCopyHindiToEnglish}
                                            title="Copy selected Hindi files to English"
                                            className="text-xs font-semibold px-2.5 py-1.5 bg-white dark:bg-slate-800 hover:bg-blue-50 border border-slate-200 dark:border-slate-700 text-blue-600 dark:text-blue-400 rounded-lg transition-colors flex items-center gap-1 shadow-xs"
                                        >
                                            <Copy size={12} />
                                            Hindi → English
                                        </button>
                                        <button
                                            type="button"
                                            onClick={handleCopyEnglishToHindi}
                                            title="Copy selected English files to Hindi"
                                            className="text-xs font-semibold px-2.5 py-1.5 bg-white dark:bg-slate-800 hover:bg-emerald-50 border border-slate-200 dark:border-slate-700 text-emerald-600 dark:text-emerald-400 rounded-lg transition-colors flex items-center gap-1 shadow-xs"
                                        >
                                            <Copy size={12} />
                                            English → Hindi
                                        </button>
                                    </div>
                                </div>

                                <input
                                    type="file"
                                    accept="image/*,video/*"
                                    multiple
                                    onChange={e => {
                                        const files = Array.from(e.target.files);
                                        if (files.length > 0) {
                                            handleUploadBothLanguages(files);
                                            e.target.value = '';
                                        }
                                    }}
                                    className="w-full text-xs file:mr-3 file:py-2 file:px-4 file:rounded-xl file:border-0 file:bg-gradient-to-r file:from-blue-600 file:to-emerald-600 file:text-white file:font-semibold hover:file:opacity-90 cursor-pointer border border-dashed border-blue-300 dark:border-blue-700 rounded-xl p-2.5 bg-white dark:bg-slate-900"
                                />
                            </div>

                            {/* DUAL LANGUAGE UPLOAD CONTAINER */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {/* ================= HINDI VARIANT ================= */}
                                <div className="bg-blue-50/40 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-900/40 rounded-2xl p-5 space-y-4">
                                    <div className="flex items-center justify-between">
                                        <h4 className="font-bold text-sm text-blue-900 dark:text-blue-200 flex items-center gap-2">
                                            <span className="w-2.5 h-2.5 rounded-full bg-blue-600"></span>
                                            Hindi Variant (हिंदी)
                                        </h4>
                                        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-blue-100 text-blue-700 dark:bg-blue-900/60 dark:text-blue-300">
                                            Hindi
                                        </span>
                                    </div>

                                    {/* Existing Hindi media if editing */}
                                    {editingBusinessData && editingBusinessData.Hindi && (editingBusinessData.Hindi.images?.length > 0 || editingBusinessData.Hindi.videos?.length > 0) && (
                                        <div>
                                            <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1.5">
                                                Existing Hindi Media ({editingBusinessData.Hindi.images?.length || 0} imgs, {editingBusinessData.Hindi.videos?.length || 0} vids)
                                            </label>
                                            <div className="flex gap-2 flex-wrap max-h-36 overflow-y-auto p-2 bg-white dark:bg-slate-900 rounded-xl border border-blue-100 dark:border-blue-900/30">
                                                {renderMediaThumbnails(editingBusinessData.Hindi.images, false, 'H', 'bg-blue-600', editingBusinessId, true)}
                                                {renderMediaThumbnails(editingBusinessData.Hindi.videos, true, 'H', 'bg-blue-600', editingBusinessId, true)}
                                            </div>
                                        </div>
                                    )}

                                    {/* Hindi Upload Input */}
                                    <div>
                                        <label className="block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1.5">
                                            Add New Images / Videos (Hindi)
                                        </label>
                                        <input
                                            type="file"
                                            accept="image/*,video/*"
                                            multiple
                                            onChange={e => {
                                                const files = Array.from(e.target.files);
                                                setSelectedHindiFiles(prev => [...prev, ...files]);
                                            }}
                                            className="w-full text-xs file:mr-3 file:py-2 file:px-3 file:rounded-xl file:border-0 file:bg-blue-600 file:text-white file:font-semibold hover:file:bg-blue-700 cursor-pointer border border-dashed border-blue-300 dark:border-blue-800 rounded-xl p-2 bg-white dark:bg-slate-900"
                                        />
                                    </div>

                                    {/* New Hindi Files Previews */}
                                    {selectedHindiFiles.length > 0 && (
                                        <div>
                                            <span className="text-xs font-semibold text-blue-700 dark:text-blue-300 block mb-1.5">
                                                Selected for Upload ({selectedHindiFiles.length}):
                                            </span>
                                            <div className="flex gap-2 flex-wrap max-h-36 overflow-y-auto">
                                                {selectedHindiFiles.map((file, idx) => {
                                                    const isVid = file.type && file.type.startsWith('video/');
                                                    const objectUrl = URL.createObjectURL(file);
                                                    return (
                                                        <div key={idx} className="relative group w-14 h-14 rounded-lg overflow-hidden border border-blue-300 shadow-sm bg-black flex-shrink-0">
                                                            {isVid ? (
                                                                <video src={objectUrl} className="w-full h-full object-cover" />
                                                            ) : (
                                                                <img src={objectUrl} alt="preview" className="w-full h-full object-cover" />
                                                            )}
                                                            <button
                                                                type="button"
                                                                onClick={() => setSelectedHindiFiles(prev => prev.filter((_, i) => i !== idx))}
                                                                className="absolute top-1 right-1 bg-rose-600 text-white p-0.5 rounded-full hover:bg-rose-700 z-10"
                                                            >
                                                                <X size={12} strokeWidth={3} />
                                                            </button>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* ================= ENGLISH VARIANT ================= */}
                                <div className="bg-emerald-50/40 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-900/40 rounded-2xl p-5 space-y-4">
                                    <div className="flex items-center justify-between">
                                        <h4 className="font-bold text-sm text-emerald-900 dark:text-emerald-200 flex items-center gap-2">
                                            <span className="w-2.5 h-2.5 rounded-full bg-emerald-600"></span>
                                            English Variant (English)
                                        </h4>
                                        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-emerald-100 text-emerald-700 dark:bg-emerald-900/60 dark:text-emerald-300">
                                            English
                                        </span>
                                    </div>

                                    {/* Existing English media if editing */}
                                    {editingBusinessData && editingBusinessData.English && (editingBusinessData.English.images?.length > 0 || editingBusinessData.English.videos?.length > 0) && (
                                        <div>
                                            <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1.5">
                                                Existing English Media ({editingBusinessData.English.images?.length || 0} imgs, {editingBusinessData.English.videos?.length || 0} vids)
                                            </label>
                                            <div className="flex gap-2 flex-wrap max-h-36 overflow-y-auto p-2 bg-white dark:bg-slate-900 rounded-xl border border-emerald-100 dark:border-emerald-900/30">
                                                {renderMediaThumbnails(editingBusinessData.English.images, false, 'E', 'bg-emerald-600', editingBusinessId, true)}
                                                {renderMediaThumbnails(editingBusinessData.English.videos, true, 'E', 'bg-emerald-600', editingBusinessId, true)}
                                            </div>
                                        </div>
                                    )}

                                    {/* English Upload Input */}
                                    <div>
                                        <label className="block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1.5">
                                            Add New Images / Videos (English)
                                        </label>
                                        <input
                                            type="file"
                                            accept="image/*,video/*"
                                            multiple
                                            onChange={e => {
                                                const files = Array.from(e.target.files);
                                                setSelectedEnglishFiles(prev => [...prev, ...files]);
                                            }}
                                            className="w-full text-xs file:mr-3 file:py-2 file:px-3 file:rounded-xl file:border-0 file:bg-emerald-600 file:text-white file:font-semibold hover:file:bg-emerald-700 cursor-pointer border border-dashed border-emerald-300 dark:border-emerald-800 rounded-xl p-2 bg-white dark:bg-slate-900"
                                        />
                                    </div>

                                    {/* New English Files Previews */}
                                    {selectedEnglishFiles.length > 0 && (
                                        <div>
                                            <span className="text-xs font-semibold text-emerald-700 dark:text-emerald-300 block mb-1.5">
                                                Selected for Upload ({selectedEnglishFiles.length}):
                                            </span>
                                            <div className="flex gap-2 flex-wrap max-h-36 overflow-y-auto">
                                                {selectedEnglishFiles.map((file, idx) => {
                                                    const isVid = file.type && file.type.startsWith('video/');
                                                    const objectUrl = URL.createObjectURL(file);
                                                    return (
                                                        <div key={idx} className="relative group w-14 h-14 rounded-lg overflow-hidden border border-emerald-300 shadow-sm bg-black flex-shrink-0">
                                                            {isVid ? (
                                                                <video src={objectUrl} className="w-full h-full object-cover" />
                                                            ) : (
                                                                <img src={objectUrl} alt="preview" className="w-full h-full object-cover" />
                                                            )}
                                                            <button
                                                                type="button"
                                                                onClick={() => setSelectedEnglishFiles(prev => prev.filter((_, i) => i !== idx))}
                                                                className="absolute top-1 right-1 bg-rose-600 text-white p-0.5 rounded-full hover:bg-rose-700 z-10"
                                                            >
                                                                <X size={12} strokeWidth={3} />
                                                            </button>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Existing Default / Legacy Media if any */}
                            {editingBusinessData && editingBusinessData.Default && (editingBusinessData.Default.images?.length > 0 || editingBusinessData.Default.videos?.length > 0) && (
                                <div className="p-4 bg-slate-50 dark:bg-slate-900/60 rounded-xl border border-slate-200 dark:border-slate-700">
                                    <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1.5">
                                        Legacy / Uncategorized Media ({editingBusinessData.Default.images?.length || 0} imgs, {editingBusinessData.Default.videos?.length || 0} vids)
                                    </label>
                                    <div className="flex gap-2 flex-wrap max-h-28 overflow-y-auto">
                                        {renderMediaThumbnails(editingBusinessData.Default.images, false, 'D', 'bg-slate-500', editingBusinessId, true)}
                                        {renderMediaThumbnails(editingBusinessData.Default.videos, true, 'D', 'bg-slate-500', editingBusinessId, true)}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Modal Action Buttons */}
                        <div className="mt-8 pt-4 border-t border-slate-100 dark:border-slate-700 flex justify-end gap-3">
                            <button
                                type="button"
                                onClick={() => setIsCreateModalOpen(false)}
                                className="px-5 py-2.5 bg-slate-100 hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 rounded-xl text-sm font-medium transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleSaveBusiness}
                                className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-semibold shadow-md hover:shadow-lg transition-all"
                            >
                                {editingBusinessId ? "Update Entry" : "Save Entry"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ================= FULL BUSINESS MEDIA GALLERY MODAL ================= */}
            {galleryBusiness && (
                <div className="fixed inset-0 z-[65] flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={() => setGalleryBusiness(null)}></div>
                    <div className="bg-white dark:bg-slate-800 rounded-3xl border border-slate-200 dark:border-slate-700 w-full max-w-5xl relative z-10 shadow-2xl overflow-hidden flex flex-col max-h-[90vh] animate-in fade-in zoom-in duration-150">
                        {/* Modal Header */}
                        <div className="p-6 border-b border-slate-100 dark:border-slate-700 flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-slate-50/50 dark:bg-slate-800/50">
                            <div className="flex items-center gap-3">
                                <div className="w-12 h-12 rounded-2xl bg-blue-600 flex items-center justify-center font-bold text-xl text-white shadow-md">
                                    {galleryBusiness.business_name ? galleryBusiness.business_name.charAt(0).toUpperCase() : 'B'}
                                </div>
                                <div>
                                    <div className="flex items-center gap-2">
                                        <h3 className="text-xl font-bold text-slate-800 dark:text-slate-100">
                                            {galleryBusiness.business_name}
                                        </h3>
                                        <span className="text-[10px] font-semibold px-2.5 py-0.5 rounded-full border bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/40 dark:text-blue-300 dark:border-blue-800">
                                            Media Gallery
                                        </span>
                                    </div>
                                    <p className="text-xs text-slate-400 mt-0.5">
                                        Total {((galleryBusiness.Hindi?.images?.length || 0) + (galleryBusiness.Hindi?.videos?.length || 0) + (galleryBusiness.English?.images?.length || 0) + (galleryBusiness.English?.videos?.length || 0))} media files available
                                    </p>
                                </div>
                            </div>

                            <div className="flex items-center gap-2">
                                <button
                                    type="button"
                                    onClick={() => {
                                        const b = galleryBusiness;
                                        setGalleryBusiness(null);
                                        handleOpenAddForBusiness(b);
                                    }}
                                    className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold transition-all shadow-sm flex items-center gap-1.5"
                                >
                                    <Plus size={14} />
                                    Add Media
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setGalleryBusiness(null)}
                                    className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                                >
                                    <X size={20} />
                                </button>
                            </div>
                        </div>

                        {/* Language Filter Tabs */}
                        <div className="px-6 pt-3 border-b border-slate-100 dark:border-slate-700 flex items-center gap-2 bg-white dark:bg-slate-800">
                            <button
                                onClick={() => setGalleryTab('all')}
                                className={`px-4 py-2.5 text-xs font-bold rounded-t-xl transition-colors border-b-2 ${galleryTab === 'all'
                                        ? 'border-blue-600 text-blue-600 dark:text-blue-400 bg-blue-50/50 dark:bg-blue-950/20'
                                        : 'border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-400'
                                    }`}
                            >
                                All Media ({((galleryBusiness.Hindi?.images?.length || 0) + (galleryBusiness.Hindi?.videos?.length || 0) + (galleryBusiness.English?.images?.length || 0) + (galleryBusiness.English?.videos?.length || 0))})
                            </button>
                            <button
                                onClick={() => setGalleryTab('hindi')}
                                className={`px-4 py-2.5 text-xs font-bold rounded-t-xl transition-colors border-b-2 ${galleryTab === 'hindi'
                                        ? 'border-blue-600 text-blue-600 dark:text-blue-400 bg-blue-50/50 dark:bg-blue-950/20'
                                        : 'border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-400'
                                    }`}
                            >
                                Hindi Media ({((galleryBusiness.Hindi?.images?.length || 0) + (galleryBusiness.Hindi?.videos?.length || 0))})
                            </button>
                            <button
                                onClick={() => setGalleryTab('english')}
                                className={`px-4 py-2.5 text-xs font-bold rounded-t-xl transition-colors border-b-2 ${galleryTab === 'english'
                                        ? 'border-emerald-600 text-emerald-600 dark:text-emerald-400 bg-emerald-50/50 dark:bg-emerald-950/20'
                                        : 'border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-400'
                                    }`}
                            >
                                English Media ({((galleryBusiness.English?.images?.length || 0) + (galleryBusiness.English?.videos?.length || 0))})
                            </button>
                        </div>

                        {/* Gallery Content Grid */}
                        <div className="p-6 overflow-y-auto flex-1 custom-scrollbar">
                            {(() => {
                                let mediaItems = [];
                                if (galleryTab === 'all' || galleryTab === 'hindi') {
                                    (galleryBusiness.Hindi?.images || []).forEach(url => mediaItems.push({ url, type: 'image', lang: 'Hindi', badge: 'bg-blue-600' }));
                                    (galleryBusiness.Hindi?.videos || []).forEach(url => mediaItems.push({ url, type: 'video', lang: 'Hindi', badge: 'bg-blue-600' }));
                                }
                                if (galleryTab === 'all' || galleryTab === 'english') {
                                    (galleryBusiness.English?.images || []).forEach(url => mediaItems.push({ url, type: 'image', lang: 'English', badge: 'bg-emerald-600' }));
                                    (galleryBusiness.English?.videos || []).forEach(url => mediaItems.push({ url, type: 'video', lang: 'English', badge: 'bg-emerald-600' }));
                                }

                                if (mediaItems.length === 0) {
                                    return (
                                        <div className="py-16 text-center text-slate-400">
                                            <FolderOpen size={48} className="mx-auto mb-3 opacity-30" />
                                            <p className="font-semibold text-sm">No media files found in this category</p>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    const b = galleryBusiness;
                                                    setGalleryBusiness(null);
                                                    handleOpenAddForBusiness(b);
                                                }}
                                                className="mt-3 text-xs font-bold text-blue-600 hover:underline"
                                            >
                                                + Upload Media Now
                                            </button>
                                        </div>
                                    );
                                }

                                return (
                                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                                        {mediaItems.map((item, idx) => (
                                            <div key={idx} className="group relative rounded-2xl overflow-hidden border border-slate-200 dark:border-slate-700 bg-slate-900 shadow-sm aspect-square flex items-center justify-center">
                                                {item.type === 'video' ? (
                                                    <div
                                                        className="w-full h-full cursor-pointer relative"
                                                        onClick={() => setPreviewMedia({ url: item.url, type: 'video' })}
                                                    >
                                                        <video src={item.url} className="w-full h-full object-cover" />
                                                        <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                                                            <Video size={22} className="text-white drop-shadow" />
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <img
                                                        src={item.url}
                                                        alt="gallery-item"
                                                        onClick={() => setPreviewMedia({ url: item.url, type: 'image' })}
                                                        className="w-full h-full object-cover cursor-pointer group-hover:scale-105 transition-transform duration-200"
                                                    />
                                                )}

                                                {/* Language Badge */}
                                                <span className={`absolute top-2 left-2 text-[10px] font-bold text-white px-2 py-0.5 rounded-md shadow ${item.badge}`}>
                                                    {item.lang}
                                                </span>

                                                {/* Overlay on Hover */}
                                                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2 pointer-events-none">
                                                    <button
                                                        type="button"
                                                        onClick={() => setPreviewMedia({ url: item.url, type: item.type })}
                                                        className="pointer-events-auto p-2 bg-white/90 text-slate-800 rounded-full hover:bg-white hover:scale-110 transition-transform shadow-md"
                                                        title="View Full Size"
                                                    >
                                                        <Eye size={16} />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            handleRemoveMedia(galleryBusiness.business_id_PK, item.url, item.type);
                                                            // Also update local galleryBusiness state immediately
                                                            setGalleryBusiness(prev => {
                                                                if (!prev) return prev;
                                                                const updated = JSON.parse(JSON.stringify(prev));
                                                                const targetLang = item.lang;
                                                                const targetArray = item.type === 'video' ? 'videos' : 'images';
                                                                if (updated[targetLang] && updated[targetLang][targetArray]) {
                                                                    updated[targetLang][targetArray] = updated[targetLang][targetArray].filter(u => u !== item.url);
                                                                }
                                                                return updated;
                                                            });
                                                        }}
                                                        className="pointer-events-auto p-2 bg-rose-600 text-white rounded-full hover:bg-rose-700 hover:scale-110 transition-transform shadow-md"
                                                        title="Delete Media File"
                                                    >
                                                        <Trash2 size={16} />
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                );
                            })()}
                        </div>
                    </div>
                </div>
            )}

            {/* ================= FULL MEDIA PREVIEW MODAL ================= */}
            {previewMedia && (
                <div
                    className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-slate-900/80 backdrop-blur-sm"
                    onClick={() => setPreviewMedia(null)}
                >
                    <div className="relative max-w-4xl max-h-[90vh] flex flex-col items-center">
                        <button
                            onClick={(e) => { e.stopPropagation(); setPreviewMedia(null); }}
                            className="absolute -top-4 -right-4 p-2 bg-white text-slate-800 rounded-full hover:bg-slate-200 transition-colors shadow-lg z-10"
                        >
                            <X size={20} />
                        </button>
                        {previewMedia.type === 'video' ? (
                            <video
                                src={previewMedia.url}
                                controls
                                autoPlay
                                className="w-auto h-auto max-w-full max-h-[85vh] rounded-2xl shadow-2xl bg-black"
                                onClick={(e) => e.stopPropagation()}
                            />
                        ) : (
                            <img
                                src={previewMedia.url}
                                alt="Preview"
                                className="w-auto h-auto max-w-full max-h-[85vh] rounded-2xl shadow-2xl object-contain bg-slate-900/40"
                                onClick={(e) => e.stopPropagation()}
                            />
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
